import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { SitePublic } from '@wooconnect/shared';
import { useAuthStore } from '@/stores/authStore';
import { useUIStore } from '@/stores/uiStore';
import { useProfile, useUpdateProfile, useChangePassword, usePreferences, useUpdatePreferences } from '@/hooks/useSettings';
import { useConnectSite, useUpdateSite, useDeleteSite, useTestSiteConnection } from '@/hooks/useAdmin';
import { useNotificationPreferences, useUpdateNotificationPreferences } from '@/hooks/useNotifications';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Skeleton } from '@/components/ui/Skeleton';
import { cn } from '@/utils/cn';
import { formatDate } from '@/utils/format';

type SettingsTab = 'profile' | 'stores' | 'notifications' | 'appearance';

const profileSchema = z.object({ firstName: z.string().min(1), lastName: z.string().min(1), avatarUrl: z.string().optional(), phone: z.string().optional(), company: z.string().optional() });
const createPasswordSchema = (msg: string) => z.object({ currentPassword: z.string().min(8), newPassword: z.string().min(8), confirmPassword: z.string().min(8) }).refine((d) => d.newPassword === d.confirmPassword, { message: msg, path: ['confirmPassword'] });

const INITIAL_STORE_FORM = { name: '', url: '', wcConsumerKey: '', wcConsumerSecret: '', bridgeApiKey: '' };

const NOTIF_PREF_KEYS = ['newOrder', 'orderStatusChanged', 'lowStock', 'refundRequest', 'productBackorder', 'dailySummary'] as const;

const DATE_FORMAT_OPTIONS = [
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' },
];

const PER_PAGE_OPTIONS = [
  { value: '10', label: '10' },
  { value: '20', label: '20' },
  { value: '50', label: '50' },
  { value: '100', label: '100' },
];

export function SettingsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, sites, loadSession, logout } = useAuthStore();
  const { theme, setTheme, language, setLanguage } = useUIStore();
  const [tab, setTab] = useState<SettingsTab>('profile');
  const [logoutConfirm, setLogoutConfirm] = useState(false);

  const { data: profile, isLoading: loadingProfile } = useProfile();
  const { data: preferences } = usePreferences();
  const updatePrefs = useUpdatePreferences();
  const updateProfile = useUpdateProfile();
  const changePassword = useChangePassword();
  const connectSite = useConnectSite();
  const updateSite = useUpdateSite();
  const deleteSite = useDeleteSite();
  const testConnection = useTestSiteConnection();

  const activeSiteId = useUIStore((s) => s.activeSiteId);
  const { data: notifPrefs, isLoading: loadingNotifPrefs } = useNotificationPreferences(activeSiteId);
  const updateNotifPrefs = useUpdateNotificationPreferences(activeSiteId);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const [storeModal, setStoreModal] = useState<{ mode: 'add' | 'edit'; siteId?: string } | null>(null);
  const [storeForm, setStoreForm] = useState(INITIAL_STORE_FORM);
  const [storeErrors, setStoreErrors] = useState<Record<string, string>>({});
  const [disconnectConfirm, setDisconnectConfirm] = useState<{ id: string; name: string } | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);

  const profileForm = useForm({ resolver: zodResolver(profileSchema), defaultValues: { firstName: '', lastName: '', avatarUrl: '', phone: '', company: '' } });
  const passwordForm = useForm({ resolver: zodResolver(createPasswordSchema(t('settings.passwordsMustMatch'))), defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' } });

  useEffect(() => {
    if (profile) profileForm.reset({ firstName: profile.firstName, lastName: profile.lastName, avatarUrl: profile.avatarUrl ?? '', phone: profile.phone ?? '', company: profile.company ?? '' });
  }, [profile, profileForm]);

  const handleProfileSave = profileForm.handleSubmit(async (data) => { await updateProfile.mutateAsync(data); });
  const handlePasswordChange = passwordForm.handleSubmit(async (data) => { await changePassword.mutateAsync({ currentPassword: data.currentPassword, newPassword: data.newPassword }); passwordForm.reset(); });
  const handleLanguageChange = (lang: string) => { setLanguage(lang); i18n.changeLanguage(lang); };

  const isSuperAdmin = user?.role === 'super_admin';

  const validateStore = () => {
    const errs: Record<string, string> = {};
    if (!storeForm.name.trim()) errs.name = t('common.required');
    if (!storeForm.url.trim() || !storeForm.url.startsWith('https://')) errs.url = t('sites.urlMustBeHttps');
    if (storeModal?.mode === 'add') {
      if (!storeForm.wcConsumerKey.trim() || !storeForm.wcConsumerKey.startsWith('ck_')) errs.wcConsumerKey = t('sites.invalidWcKey');
      if (!storeForm.wcConsumerSecret.trim() || !storeForm.wcConsumerSecret.startsWith('cs_')) errs.wcConsumerSecret = t('sites.invalidWcSecret');
    }
    setStoreErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleStoreSubmit = async () => {
    if (!validateStore()) return;
    if (storeModal?.mode === 'add') {
      await connectSite.mutateAsync({
        name: storeForm.name, url: storeForm.url,
        wcConsumerKey: storeForm.wcConsumerKey, wcConsumerSecret: storeForm.wcConsumerSecret,
        bridgeApiKey: storeForm.bridgeApiKey || undefined,
      });
    } else if (storeModal?.mode === 'edit' && storeModal.siteId) {
      const data: Record<string, string> = { name: storeForm.name, url: storeForm.url };
      if (storeForm.wcConsumerKey) data.wcConsumerKey = storeForm.wcConsumerKey;
      if (storeForm.wcConsumerSecret) data.wcConsumerSecret = storeForm.wcConsumerSecret;
      if (storeForm.bridgeApiKey) data.bridgeApiKey = storeForm.bridgeApiKey;
      await updateSite.mutateAsync({ siteId: storeModal.siteId, data });
    }
    setStoreModal(null);
    setStoreForm(INITIAL_STORE_FORM);
    setStoreErrors({});
    loadSession();
  };

  const handleDisconnect = async () => {
    if (!disconnectConfirm) return;
    await deleteSite.mutateAsync(disconnectConfirm.id);
    setDisconnectConfirm(null);
    loadSession();
  };

  const handleTest = async (siteId: string) => {
    setTestingId(siteId);
    try {
      await testConnection.mutateAsync(siteId);
    } finally {
      setTestingId(null);
    }
  };

  const openEditStore = (site: SitePublic) => {
    setStoreForm({ name: site.name, url: site.url, wcConsumerKey: '', wcConsumerSecret: '', bridgeApiKey: '' });
    setStoreModal({ mode: 'edit', siteId: site.id });
  };

  const tabs: Array<{ key: SettingsTab; label: string }> = [
    { key: 'profile', label: t('settings.profile') },
    { key: 'stores', label: t('settings.stores') },
    { key: 'notifications', label: t('settings.notificationPrefs') },
    { key: 'appearance', label: t('settings.appearance') },
  ];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('settings.title')}</h1>

      {/* Tab nav */}
      <div className="flex gap-1 overflow-x-auto rounded-lg bg-gray-100 p-1 dark:bg-gray-800">
        {tabs.map((t_) => (
          <button key={t_.key} onClick={() => setTab(t_.key)} className={cn('whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium transition-colors', tab === t_.key ? 'bg-white shadow-sm dark:bg-gray-700 dark:text-gray-100' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200')}>
            {t_.label}
          </button>
        ))}
      </div>

      {/* Profile Tab */}
      {tab === 'profile' && (
        <div className="space-y-6">
          <Card title={t('settings.profile')}>
            {loadingProfile ? <Skeleton className="h-40 w-full" /> : (
              <form onSubmit={handleProfileSave} className="space-y-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Input label={t('settings.firstName')} {...profileForm.register('firstName')} error={profileForm.formState.errors.firstName?.message} />
                  <Input label={t('settings.lastName')} {...profileForm.register('lastName')} error={profileForm.formState.errors.lastName?.message} />
                </div>
                <Input label={t('auth.email')} value={profile?.email ?? ''} disabled />
                <p className="text-xs text-gray-500 dark:text-gray-400">{t('settings.emailReadOnly')}</p>
                <Input label={t('common.phone')} {...profileForm.register('phone')} />
                <Input label={t('common.company')} {...profileForm.register('company')} />
                <Input label={t('settings.avatarUrl')} {...profileForm.register('avatarUrl')} />
                <div className="flex justify-end">
                  <Button type="submit" loading={updateProfile.isPending}>{t('common.save')}</Button>
                </div>
              </form>
            )}
          </Card>

          <Card title={t('auth.changePassword')}>
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <Input label={t('auth.currentPassword')} type="password" {...passwordForm.register('currentPassword')} error={passwordForm.formState.errors.currentPassword?.message} />
              <Input label={t('auth.newPassword')} type="password" {...passwordForm.register('newPassword')} error={passwordForm.formState.errors.newPassword?.message} />
              <Input label={t('auth.confirmPassword')} type="password" {...passwordForm.register('confirmPassword')} error={passwordForm.formState.errors.confirmPassword?.message} />
              <div className="flex justify-end">
                <Button type="submit" loading={changePassword.isPending}>{t('auth.changePassword')}</Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      {/* Stores Tab */}
      {tab === 'stores' && (
        <div className="space-y-4">
          {isSuperAdmin && (
            <div className="flex justify-end">
              <Button onClick={() => { setStoreForm(INITIAL_STORE_FORM); setStoreErrors({}); setStoreModal({ mode: 'add' }); }}>{t('sites.addStore')}</Button>
            </div>
          )}

          {sites.length > 0 ? sites.map((site) => (
            <Card key={site.id}>
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex items-start gap-3">
                  <span className={cn('mt-1 h-3 w-3 flex-shrink-0 rounded-full', site.status === 'active' ? 'bg-green-500' : site.status === 'error' ? 'bg-red-500' : 'bg-gray-400')} />
                  <div>
                    <p className="font-medium text-gray-900 dark:text-gray-100">{site.name}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{site.url}</p>
                    <div className="mt-2 flex flex-wrap gap-2 text-xs text-gray-500 dark:text-gray-400">
                      {site.wcVersion && <span>WC {site.wcVersion}</span>}
                      {site.wpVersion && <span>WP {site.wpVersion}</span>}
                      {site.bridgeVersion && <span>{t('sites.bridgeLabel', { version: site.bridgeVersion })}</span>}
                      {site.currency && <span>{t('sites.currency')}: {site.currency}</span>}
                      {site.timezone && <span>{t('sites.timezone')}: {site.timezone}</span>}
                    </div>
                    {site.createdAt && <p className="mt-1 text-xs text-gray-400">{t('sites.connectedSince')}: {formatDate(site.createdAt)}</p>}
                  </div>
                </div>
                <div className="flex flex-shrink-0 gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleTest(site.id)} loading={testingId === site.id}>{t('sites.testConnection')}</Button>
                  {isSuperAdmin && (
                    <>
                      <Button variant="outline" size="sm" onClick={() => openEditStore(site)}>{t('common.edit')}</Button>
                      <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700 dark:text-red-400" onClick={() => setDisconnectConfirm({ id: site.id, name: site.name })}>{t('sites.disconnect')}</Button>
                    </>
                  )}
                </div>
              </div>
            </Card>
          )) : (
            <Card><p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">{t('sites.noSites')}</p></Card>
          )}
        </div>
      )}

      {/* Notifications Tab */}
      {tab === 'notifications' && (
        <Card title={t('notifications.preferences')}>
          {!activeSiteId ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('dashboard.selectStore')}</p>
          ) : loadingNotifPrefs ? (
            <div className="space-y-3">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-10 w-full rounded" />)}</div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('notifications.prefsDescription')}</p>
              {NOTIF_PREF_KEYS.map((key) => (
                <label key={key} className="flex items-center justify-between rounded-lg border border-gray-200 px-4 py-3 dark:border-gray-700">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t(`notifications.${key}`)}</span>
                  <input
                    type="checkbox"
                    checked={notifPrefs?.[key] ?? true}
                    onChange={(e) => updateNotifPrefs.mutate({ [key]: e.target.checked })}
                    className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                  />
                </label>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Appearance Tab */}
      {tab === 'appearance' && (
        <div className="space-y-6">
          <Card title={t('settings.theme')}>
            <div className="grid grid-cols-3 gap-3">
              {(['light', 'dark', 'system'] as const).map((opt) => (
                <button key={opt} onClick={() => setTheme(opt)} className={cn('rounded-lg border-2 p-4 text-center text-sm font-medium transition-colors', theme === opt ? 'border-brand-500 bg-brand-50 text-brand-700 dark:border-brand-400 dark:bg-brand-900/20 dark:text-brand-400' : 'border-gray-200 text-gray-600 hover:border-gray-300 dark:border-gray-700 dark:text-gray-300')}>
                  {t(`settings.theme${opt.charAt(0).toUpperCase() + opt.slice(1)}`)}
                </button>
              ))}
            </div>
          </Card>

          <Card title={t('settings.language')}>
            <div className="grid grid-cols-3 gap-3">
              {[{ code: 'en', label: 'English' }, { code: 'el', label: 'Ellinika' }, { code: 'de', label: 'Deutsch' }].map((lang) => (
                <button key={lang.code} onClick={() => handleLanguageChange(lang.code)} className={cn('rounded-lg border-2 p-4 text-center text-sm font-medium transition-colors', language === lang.code ? 'border-brand-500 bg-brand-50 text-brand-700 dark:border-brand-400 dark:bg-brand-900/20 dark:text-brand-400' : 'border-gray-200 text-gray-600 hover:border-gray-300 dark:border-gray-700 dark:text-gray-300')}>
                  {lang.label}
                </button>
              ))}
            </div>
          </Card>

          <Card title={t('settings.dateFormat')}>
            <Select options={DATE_FORMAT_OPTIONS} value={preferences?.dateFormat ?? 'DD/MM/YYYY'} onChange={(v) => updatePrefs.mutate({ dateFormat: v })} />
          </Card>

          <Card title={t('settings.itemsPerPage')}>
            <Select options={PER_PAGE_OPTIONS} value={String(preferences?.itemsPerPage ?? 20)} onChange={(v) => updatePrefs.mutate({ itemsPerPage: Number(v) })} />
          </Card>
        </div>
      )}

      {/* Logout */}
      <Card>
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-gray-900 dark:text-gray-100">{t('settings.session')}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('settings.confirmLogoutMessage')}</p>
          </div>
          <Button variant="danger" onClick={() => setLogoutConfirm(true)}>{t('auth.logout')}</Button>
        </div>
      </Card>

      <ConfirmDialog isOpen={logoutConfirm} onClose={() => setLogoutConfirm(false)} onConfirm={handleLogout} title={t('auth.logout')} message={t('settings.confirmLogout')} confirmLabel={t('auth.logout')} confirmVariant="danger" />

      {/* Add/Edit Store Modal */}
      <Modal isOpen={!!storeModal} onClose={() => { setStoreModal(null); setStoreErrors({}); }} title={storeModal?.mode === 'edit' ? t('sites.editStore') : t('sites.addStore')} size="md">
        <div className="space-y-4">
          <Input label={t('settings.storeName')} value={storeForm.name} onChange={(e) => setStoreForm((f) => ({ ...f, name: e.target.value }))} error={storeErrors.name} />
          <Input label={t('settings.storeUrl')} value={storeForm.url} onChange={(e) => setStoreForm((f) => ({ ...f, url: e.target.value }))} placeholder="https://your-store.com" error={storeErrors.url} />
          <Input label={t('settings.wcKey')} value={storeForm.wcConsumerKey} onChange={(e) => setStoreForm((f) => ({ ...f, wcConsumerKey: e.target.value }))} placeholder="ck_..." error={storeErrors.wcConsumerKey} />
          <Input label={t('settings.wcSecret')} value={storeForm.wcConsumerSecret} onChange={(e) => setStoreForm((f) => ({ ...f, wcConsumerSecret: e.target.value }))} placeholder="cs_..." error={storeErrors.wcConsumerSecret} />
          <Input label={`${t('settings.bridgeKey')} (${t('common.optional')})`} value={storeForm.bridgeApiKey} onChange={(e) => setStoreForm((f) => ({ ...f, bridgeApiKey: e.target.value }))} />
          {storeModal?.mode === 'edit' && (
            <p className="text-xs text-gray-400 dark:text-gray-500">{t('settings.wcKey')}/{t('settings.wcSecret')}: leave empty to keep current</p>
          )}
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => { setStoreModal(null); setStoreErrors({}); }}>{t('common.cancel')}</Button>
            <Button onClick={handleStoreSubmit} loading={connectSite.isPending || updateSite.isPending}>
              {storeModal?.mode === 'edit' ? t('common.save') : t('common.connect')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Disconnect store confirmation */}
      <ConfirmDialog isOpen={!!disconnectConfirm} onClose={() => setDisconnectConfirm(null)} onConfirm={handleDisconnect} title={t('sites.disconnect')} message={t('sites.confirmDisconnect')} confirmLabel={t('common.disconnect')} confirmVariant="danger" loading={deleteSite.isPending} />
    </div>
  );
}
